import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../../../store/selectors';
import { Slide, ToastContainer } from 'react-toastify';
import { createFlowOfFundsMutationToast } from '../../../../../components/FlowOfFunds/flowOfFundsAutoSaveToast';
import '../../../../../components/AutoSaveToast/AutoSaveToast.scss';
import { LegalFees } from '../../../../../components/FlowOfFunds/LegalFees/LegalFees';
import { WireInstructionsModal } from '../../../../../components/FlowOfFunds/WireInstructionsModal/WireInstructionsModal';
import { ClosingTickler } from '../../../../../components/FlowOfFunds/ClosingTickler/ClosingTickler';
import { useFlowOfFunds } from '../useFlowOfFunds';
import {
    ApprovalState,
    FeeAgreementParty,
    WireInstructions,
} from '../../../../../models/FlowOfFunds/flow-of-funds.model';
import createNotification from '../../../../../utils/createNotification';
import { NotificationFlagsCategories } from '../../../../../models/NotificationFlags/NotificationFlagsCategories';
import { addNotificationFlagToDelete } from '../../../../../slices/notification-flags/notification-flags.slice';
import { GetFlowOfFunds } from '../../../../../slices/marketplace/flow-of-funds/thunks/get-flow-of-funds.thunk';
import { SetLegalFeesAmount } from '../../../../../slices/marketplace/flow-of-funds/thunks/set-legal-fees-amount.thunk';
import { UploadLegalFeesInvoice } from '../../../../../slices/marketplace/flow-of-funds/thunks/upload-legal-fees-invoice.thunk';
import { DeleteLegalFeesInvoice } from '../../../../../slices/marketplace/flow-of-funds/thunks/delete-legal-fees-invoice.thunk';
import { DownloadLegalFeesInvoice } from '../../../../../slices/marketplace/flow-of-funds/thunks/download-legal-fees-invoice.thunk';
import { UpdateLegalFeesWireInstructions } from '../../../../../slices/marketplace/flow-of-funds/thunks/update-legal-fees-wire-instructions.thunk';
import { SetLegalFeesWireInstructionsVerification } from '../../../../../slices/marketplace/flow-of-funds/thunks/set-legal-fees-wire-instructions-verification.thunk';
import { SetLegalFeesAgreement } from '../../../../../slices/marketplace/flow-of-funds/thunks/set-legal-fees-agreement.thunk';

export interface LegalFeesSubTabProps {
    engagementId: number;
}

const LEGAL_FEES_AUTOSAVE_TOAST_CONTAINER_ID = 'legal-fees-autosave-toast-container';

const legalFeesMutationToast = createFlowOfFundsMutationToast(
    LEGAL_FEES_AUTOSAVE_TOAST_CONTAINER_ID,
    'legal-fees-mutation'
);

const openUrlInNewTab = (url: string) => {
    const viewer = window.open(url, '_blank');
    if (viewer) {
        viewer.opener = null;
        return;
    }
    createNotification('Allow pop-ups for this site to open the document.', 'warning');
};

const downloadBlobAsFile = (blob: Blob, contentType: string, fileName: string) => {
    const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

const previewBlobInNewTab = (blob: Blob, contentType: string) => {
    const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }));
    openUrlInNewTab(url);
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

export const LegalFeesSubTab = (props: LegalFeesSubTabProps) => {
    const dispatch = useDispatch() as any;
    const { flowOfFunds, isLoading } = useFlowOfFunds(props.engagementId);
    const [isWireInstructionsOpen, setIsWireInstructionsOpen] = useState(false);
    const notificationFlags = useStateSelector((state) => state.notificationFlags.flags);
    const notificationFlagsRef = useRef(notificationFlags);
    notificationFlagsRef.current = notificationFlags;

    const engagementId = props.engagementId;

    // Reading the sub-tab marks its notifications read, and they are cleared when the
    // user leaves it — so every badge stays put for as long as they are on the tab.
    // Same shape as the legal-docs flags in Profile.js.
    useEffect(() => {
        const prefix = `${NotificationFlagsCategories.MpCode}.${engagementId}.${NotificationFlagsCategories.FlowOfFundsCode}.${NotificationFlagsCategories.LegalFeesCode}`;
        return () => {
            notificationFlagsRef.current
                ?.filter((flag: any) =>
                    flag.eventCode.toLowerCase().startsWith(prefix.toLowerCase())
                )
                .forEach((flag: any) =>
                    dispatch(addNotificationFlagToDelete({ ...flag, refreshAfterRemoval: true }))
                );
        };
    }, [engagementId]);

    if (isLoading) {
        return <div className="mt-4 text-muted">Loading…</div>;
    }
    if (!flowOfFunds?.legalFees) {
        return (
            <div className="mt-4 text-muted">
                No legal fees have been recorded for this engagement yet.
            </div>
        );
    }

    const legalFees = flowOfFunds.legalFees;

    const refreshOnRejection = (action: any, isFulfilled: (action: any) => boolean) => {
        if (!isFulfilled(action)) {
            dispatch(GetFlowOfFunds(engagementId));
        }
        return action;
    };

    const mutateWithSaveToast = async (
        actionPromise: Promise<any>,
        isFulfilled: (action: any) => boolean
    ) => {
        legalFeesMutationToast.saving();
        const action = await actionPromise;
        if (isFulfilled(action)) {
            legalFeesMutationToast.saved();
        } else {
            legalFeesMutationToast.failed();
            dispatch(GetFlowOfFunds(engagementId));
        }
        return action;
    };

    const openInvoice = async (mode: 'preview' | 'download') => {
        const invoiceDocument = legalFees.invoiceDocument;
        if (!invoiceDocument) {
            return;
        }
        const action = await dispatch(DownloadLegalFeesInvoice({ engagementId }));
        if (!DownloadLegalFeesInvoice.fulfilled.match(action)) {
            return;
        }
        if (mode === 'preview') {
            previewBlobInNewTab(action.payload, invoiceDocument.contentType);
        } else {
            downloadBlobAsFile(action.payload, invoiceDocument.contentType, invoiceDocument.fileName);
        }
    };

    return (
        <div className="mt-4">
            <div className="ff-tickler-slot">
                <ClosingTickler closing={flowOfFunds.closing} />
            </div>

            <LegalFees
                engagementId={engagementId}
                legalFees={legalFees}
                onAmountChange={async (amount) => {
                    await mutateWithSaveToast(
                        dispatch(SetLegalFeesAmount({ engagementId, amountOwedAtClose: amount })),
                        SetLegalFeesAmount.fulfilled.match
                    );
                }}
                onInvoiceUpload={async (file) => {
                    await mutateWithSaveToast(
                        dispatch(UploadLegalFeesInvoice({ engagementId, file })),
                        UploadLegalFeesInvoice.fulfilled.match
                    );
                }}
                onInvoiceDelete={async () => {
                    await mutateWithSaveToast(
                        dispatch(DeleteLegalFeesInvoice({ engagementId })),
                        DeleteLegalFeesInvoice.fulfilled.match
                    );
                }}
                onInvoicePreview={() => openInvoice('preview')}
                onInvoiceDownload={() => openInvoice('download')}
                onOpenWireInstructions={() => setIsWireInstructionsOpen(true)}
                onVerifyWireInstructions={async (verified) => {
                    const action = await dispatch(
                        SetLegalFeesWireInstructionsVerification({ engagementId, verified })
                    );
                    refreshOnRejection(action, SetLegalFeesWireInstructionsVerification.fulfilled.match);
                }}
                onSeatStateChange={async (party: FeeAgreementParty, targetState: ApprovalState) => {
                    await mutateWithSaveToast(
                        dispatch(SetLegalFeesAgreement({ engagementId, targetState })),
                        SetLegalFeesAgreement.fulfilled.match
                    );
                }}
            />

            <WireInstructionsModal
                isOpen={isWireInstructionsOpen}
                instructions={legalFees.wireInstructions}
                isReadOnly={!legalFees.capabilities.canEditWireInstructions}
                onClose={() => setIsWireInstructionsOpen(false)}
                onSave={async (instructions: WireInstructions) => {
                    const action = await dispatch(
                        UpdateLegalFeesWireInstructions({ engagementId, instructions })
                    );
                    refreshOnRejection(action, UpdateLegalFeesWireInstructions.fulfilled.match);
                    return UpdateLegalFeesWireInstructions.fulfilled.match(action);
                }}
            />

            <ToastContainer
                containerId={LEGAL_FEES_AUTOSAVE_TOAST_CONTAINER_ID}
                position="bottom-right"
                transition={Slide}
                className="auto-save-toast-container"
                newestOnTop={false}
            />
        </div>
    );
};

export default LegalFeesSubTab;
